import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output, SimpleChanges,
  ViewEncapsulation
} from '@angular/core';

import { animate, state, style, transition, trigger } from '@angular/animations';
import { AbstractControl, FormControl, ValidationErrors } from '@angular/forms';
import { Subject, of, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

function hasDuplicates(arr: any[]): boolean {
  const valueCounter = new Map();

  for (let i = 0; i < arr.length; i++) {
    const value = arr[i];

    if (valueCounter.get(value)) {
      return true;
    }

    valueCounter.set(value, 1);
  }

  return false;
}

@Component({
  selector: 'mcrai-input[type=file]',
  template: `<input
  class="mcrai-input__file-input"
  [id]="id"
  type="file"
  name="files[]"
  (change)="onChange($event)"
  data-multiple-caption="files selected"
  [multiple]="isMultiple()"
>
<label
  class="mcrai-input__dropzone"
  [class.mcrai-input__dropzone--dragover]="isDraggingOver"
  [@dropzoneSize]="isDraggingOver || !stagedFiles.length"
  [for]="id"
  mcrai-button
  (dragover)="onDragover($event)"
  (dragenter)="onDragEnter($event)"
  (dragleave)="onDragLeave($event)"
  (drop)="onDrop($event)"
>
  <p class="mcrai-input__hint">
    <strong>klikni</strong> na oblast, nebo <strong>pÅetÃ¡hni</strong> soubory.
  </p>
</label>
<div class="mcrai-input__max-file-size">maximÃ¡lnÃ­ velikost {{ getMaxFileSize() }}</div>
<div class="mcrai-input__error" *ngIf="control.invalid">
  {{ getFirstError() }}
</div>

<ul class="mcrai-input__files" *ngIf="stagedFiles.length > 0">
  <li class="mcrai-input__file" *ngFor="let file of stagedFiles">
    <div class="mcrai-input__file-info">
      <p class="mcrai-input__file-name">{{ file?.name }}</p>
      <p class="mcrai-input__file-size">{{ file?.size | fileSize }}</p>
    </div>
    <div class="mcrai-input__actions">
      <button
        (click)="removeStaged(file.name)"
        mcrai-button="negative"
      >Odstranit z fronty</button>
    </div>
  </li>
</ul>
`,
  styles: [`.mcrai-input--file{display:block}.mcrai-input__dropzone{display:flex;background-position:left top,left bottom,left top,right top;background-repeat:repeat-x,repeat-x,repeat-y,repeat-y;background-size:15px 1px,15px 1px,1px 15px,1px 15px}.mcrai-input__hint{font-style:italic;pointer-events:none;font-size:16px}.mcrai-input__file-input{width:.1px;height:.1px;opacity:0;overflow:hidden;position:absolute;z-index:-1}.mcrai-input__files{display:flex;flex-flow:row wrap;margin-top:10px}.mcrai-input__file{flex:0 0 100%;margin-bottom:2px;display:flex}.mcrai-input__file-info{flex:1}.mcrai-input__actions{flex:0 0 auto}.mcrai-input__file-size{font-size:14px}.mcrai-input__max-file-size{text-align:right;font-size:12px;margin-top:3px;font-style:italic}`],
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  animations: [
    trigger('dropzoneSize', [
      state('true', style({ height: '200px' })),
      state('false', style({ height: '80px' })),
      transition('0 => 1',
        animate(`225ms cubic-bezier(0.4,0.0,1,1)`)),
      transition('1 => 0',
        animate(`195ms cubic-bezier(0.0,0.0,0.2,1)`))
    ])
  ],
  host: {
    '[class.mcrai-input]': 'true',
    '[class.mcrai-input--file]': 'true'
  }
})
export class FileComponent implements OnInit, OnDestroy {
  private _destroy: Subject<null> = new Subject<null>();

  public readonly MAX_FILE_SIZE = Math.pow(2, 25);

  public static count: number = 0;


  @Input() multiple: boolean;
  @Input() control: FormControl;

  @Output() filesChange: EventEmitter<File | File[]> = new EventEmitter<File | File[]>();

  private _stagedFiles: File[] = [];
  get stagedFiles(): File[] {
    return this._stagedFiles;
  }

  private _id: number;
  get id(): number {
    return this._id;
  }

  private _isDraggingOver = false;
  get isDraggingOver(): boolean {
    return this._isDraggingOver;
  }

  constructor() {
    this._id = ++FileComponent.count;
  }

  ngOnInit() { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.hasOwnProperty('control') && !!changes.control.currentValue) {
      this.control.setAsyncValidators([
        this._columnValidator
      ]);

      this.control.setValidators([
        this._sizeValidator.bind(this)
      ]);

      this._destroy.next();

      this.control.valueChanges
        .pipe(
          takeUntil(this._destroy)
        )
        .subscribe((value: File | File[]) => {
          if (value) {
            this._stagedFiles = Array.isArray(value) ? value : [value]
          } else {
            this._stagedFiles = [];
          }
        });
    }
  }

  ngOnDestroy() {
    this._destroy.next();
  }

  private _queueFiles(files: File[]): void {
    if (this.control instanceof FormControl) {
      if (files.length > 0) {
        this.control.setValue(this.isMultiple() ? files : files[0]);
      } else {
        this.control.reset();
      }
    } else {
      console.warn(`Could not set controls value. ${this.control} provided as control`);
    }
  }

  private _columnValidator(control: AbstractControl): Observable<null | ValidationErrors> {
    const file = control.value;

    if (!file || file.type !== 'text/csv') {
      return of(null);
    }

    const reader = new FileReader();

    const observable = Observable.create(observer => {
      reader.onloadend = () => {
        const rows   = reader.result.split(/\r?\n/);
        const cells  = rows[0].replace(/"/g, '').split(/;/g);
        const result = hasDuplicates(cells) ? { duplicates: `Soubor '${file.name}' obsahuje duplicitnÃ­ sloupce` } : null;

        observer.next(result);
        observer.complete();
      };

      reader.onerror = error => observer.error(error);
    });

    reader.readAsText(file);

    return observable;
  }

  private _sizeValidator(control: AbstractControl): null | ValidationErrors {
    let files = control.value;

    if (!files) {
      return null;
    }

    if (!Array.isArray(files)) {
      files = [files];
    }

    for (let i = 0; i < files.length; i++) {
      if (files[i].size > this.MAX_FILE_SIZE) {
        return { size: `Soubor '${files[i].name}' pÅekraÄuje maximÃ¡lnÃ­ velikost souboru` };
      }
    }

    return null;
  }

  private _sanitazeFiles(files: FileList | File[]): File[] {
    if (files instanceof FileList) {
      files = Array.from(files);
    }

    files = this._filterOutDuplicities(files);
    files = this._filterOutCountExceedingFiles(files);

    return files;
  }

  private _filterOutDuplicities(files: File[]): File[] {
    const filesSeen = new Set;

    return files.filter(file => {
      if (!filesSeen.has(file.name)) {
        filesSeen.add(file.name);
        return true;
      } else {
        return false;
      }
    });
  }

  private _filterOutCountExceedingFiles(files: File[]): File[] {
    if (!this.isMultiple() && files.length > 1) {
      return [files[0]];
    }

    return files;
  }

  public onDragover(event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  public onDragEnter(event): void {
    event.preventDefault();
    event.stopPropagation();

    this._isDraggingOver = true;
  }

  public onDragLeave(event): void {
    event.preventDefault();
    event.stopPropagation();

    this._isDraggingOver = false;
  }

  public onDrop(event): void {
    event.preventDefault();
    event.stopPropagation();

    this._isDraggingOver = false;

    const files = this._sanitazeFiles(event.dataTransfer.files);
    this._queueFiles(files);
  }

  public onChange(event): void {
    const files = this._sanitazeFiles(event.target.files);
    this._queueFiles(files);
  }

  public isMultiple(): boolean {
    return typeof this.multiple !== 'undefined';
  }

  public removeStaged(name: string): void {
    const files = this._stagedFiles.filter(staged => staged.name !== name);
    this._queueFiles(files);
  }

  public getMaxFileSize() {
    let fileSizeInBytes = this.MAX_FILE_SIZE;
    let i = -1;
    let byteUnits = [' kB', ' MB', ' GB', ' TB', 'PB', 'EB', 'ZB', 'YB'];
    do {
      fileSizeInBytes = fileSizeInBytes / 1024;
      i++;
    } while (fileSizeInBytes > 1024);

    return Math.max(fileSizeInBytes, 0.1).toFixed(1) + byteUnits[i];
  }

  public getFirstError(): string {
    const keys = Object.keys(this.control.errors);

    return this.control.errors[keys[0]];
  }

}
